import { createSelector } from 'reselect';
import _find from 'lodash/find';
import _has from 'lodash/has';
import _every from 'lodash/every';
import _sortBy from 'lodash/sortBy';

// Selectors
import { getCards } from './cardCatalogSelector';

// Static Data
import {
  CUSTOMIZATION_BAR_DROPDOWN_SECTIONS,
  // INVITES_PRODUCT_TYPES,
  BACK_PRINTING_PRICE,
  REQUIRED_OPTION_VALUE_KEYS
} from '../constants/Cards';

const ORDERED_CUSTOMIZATION_TYPES = ['INVITATION', 'SAVE_THE_DATE', 'RSVP', 'RSVP_ENVELOPE', 'INVITATION_ENVELOPE', 'ENVELOPE'];

// routing selectors
export const getPathName = state => state.routing.locationBeforeTransitions.pathname;

// formHistory selectors
export const getFormHistory = state => state.cards.formHistory;

// customizationBar selectors
export const getAddressingOptionStatus = state => state.cards.cardCustomizationBar.hasRecipientAddressing;
export const getCustomizationErrorsByElementUUID = state => state.cards.cardCustomizationBar.errorsByElementUUID;

export const getCustomizationHasErrorsV2 = createSelector(
  getCustomizationErrorsByElementUUID,
  (errorsByElementUUID) => {
    if (errorsByElementUUID && errorsByElementUUID.length > 0) {
      return true;
    }
    return false;
  }
);

// recipients selectors
export const getSelectedGuestIds = state => state.cards.recipients.selectedGuestIds;

// cardProject selectors
export const getActiveField = state => state.cards.cardProject.activeField || '';
export const getProjectType = state => state.cards.cardProject.projectType;
export const getCustomizationTypeUUIDs = state => state.cards.cardProject.customizationTypeToUUID;
export const getCustomizations = state => state.cards.cardProject.customizations;
export const getProjectUUID = state => state.cards.cardProject.projectUUID;
export const getSteps = state => state.cards.cardProject.steps;
export const getFonts = state => state.cards.cardProject.fonts;
export const getSelectedSize = state => state.cards.cardProject.size; // DUPLICATES
export const getSize = state => state.cards.cardProject.size; // DUPLICATES
export const getRSVPCustomizationUUID = state => state.cards.cardProject.customizationTypeToUUID.RSVP;
export const getSplitOrderId = state => state.cards.cardProject.splitOrderId;

// props route selector
export const getCurrentStep = (_, props) => {
  const { route, router, routes } = props;
  if (route && route.path) {
    return props.route.path;
  } if (router && router.location) {
    return props.router.location.pathname.split('/').pop();
  } if (routes) {
    const routesLength = routes.length;
    const lastRoute = routes[routesLength - 1];
    if (lastRoute && lastRoute.path) {
      return lastRoute.path;
    }
  }
  return null;
};


export const getCardType = createSelector(
  getSteps,
  getCurrentStep,
  (steps, currentStep) => steps[currentStep].cardType
);

export const getPageNumber = createSelector(
  getSteps,
  getCurrentStep,
  (steps, currentStep) => steps[currentStep].pageNumber
);

export const getCustomizationUUID = createSelector(
  getCustomizationTypeUUIDs,
  getCardType,
  (customizationTypeToUUID, cardType) => customizationTypeToUUID[cardType]
);

export const getCustomization = createSelector(
  getCustomizations,
  getCustomizationUUID,
  (customizations, customizationUUID) => customizations[customizationUUID]
);

export const getPage = createSelector(
  getCustomization,
  getPageNumber,
  (customization, pageNumber) => _find(customization.pages, { page_number: pageNumber + 1 })
);

export const getElements = createSelector(
  getCustomization,
  getPage,
  (customization, page) => page.elementUUIDs.map(elementUUID => customization.elementsByUUID[elementUUID])
);

export const getTextElements = createSelector(
  getElements,
  elements => elements.filter(element => element.content_type === 'TEXT')
);

export const getImageElements = createSelector(
  getElements,
  elements => elements.filter(element => element.content_type === 'IMAGE')
);

export const getCustomizationVariation = createSelector(
  getCustomization,
  customization => customization && customization.variation
);

export const getReversePrintingDisabled = createSelector(
  getCustomizationVariation,
  (variation) => {
    if (variation && variation.option_values) {
      return variation.option_values['reverse-printing'] && variation.option_values['reverse-printing'] === 'false';
    }
    return false;
  }
);

export const getUuid = createSelector(
  getProjectType,
  getCustomizationTypeUUIDs,
  (type, uuids) => uuids[type],
);

export const getCustomizationsWithValidOptionValues = createSelector(
  getCustomizations,
  (customizationsByUUID) => {
    const customizations = Object.keys(customizationsByUUID).map(uuid => customizationsByUUID[uuid]);
    return customizations.filter((customization) => {
      if (customization && customization.variation) {
        const { variation } = customization;
        return _every(REQUIRED_OPTION_VALUE_KEYS, key => _has(variation.option_values, key));
      }
      return false;
    });
  }
);

export const getSkuId = createSelector(
  getUuid,
  getCustomizations,
  (uuid, customizations) => {
    if (customizations[uuid]) {
      return customizations[uuid].sku_object_id;
    }
    return null;
  }
);


export const getSelectedQuantity = createSelector(
  getUuid,
  getCustomizations,
  (uuid, customizations) => {
    if (customizations[uuid]) {
      return customizations[uuid].quantity;
    }
    return 100;
  }
);

export const getCurrentRoute = createSelector(
  getPathName,
  pathname => pathname.split('/').pop()
);

export const getIsAddressingOptionSet = createSelector(
  getCurrentRoute,
  getSteps,
  getCustomizations,
  getCustomizationTypeUUIDs,
  (route, steps, customizations, uuids) => {
    if (steps[route]) {
      const { cardType } = steps[route];
      const customization = customizations[uuids[cardType]];
      if (customization) {
        return customization.recipient_addressing;
      }
    }
    return false;
  }
);

export const getRSVPCustomization = createSelector(
  getCustomizations,
  getRSVPCustomizationUUID,
  (customizations, rsvpCustomizationUUID) => customizations[rsvpCustomizationUUID]
);

export const getIsRSVPOptionSet = createSelector(
  getCurrentRoute,
  getSteps,
  getCustomizations,
  getCustomizationTypeUUIDs,
  (route, steps, customizations, uuids) => {
    if (steps[route] && steps[route].cardType) {
      const { cardType } = steps[route];
      const customization = customizations[uuids[cardType]];
      if (customization) {
        if (_has(customization, 'inactive')) {
          return !customization.inactive;
        }
        return !customization.deleted_at;
      }
    }
    return false;
  }
);

const isCustomizationActive = customization => (
  _has(customization, 'inactive')
    ? !customization.inactive
    : !customization.deleted_at
);

export const getCustomizationActive = createSelector(
  getCustomization,
  customization => isCustomizationActive(customization)
);

export const getProjectSubtotalSummaryV2 = createSelector(
  getCustomizations,
  getCustomizationTypeUUIDs,
  getSteps,
  getCurrentStep,
  (customizations, customizationTypeToUUIDs, steps, currentStep) => {
    const sections = CUSTOMIZATION_BAR_DROPDOWN_SECTIONS.reduce((result, section) => {
      if (steps[section.step]) {
        const customizationType = steps[section.step].cardType;
        const customizationUUID = customizationTypeToUUIDs[customizationType];
        if (customizationUUID) {
          const customization = customizations[customizationUUID];
          if (customization && isCustomizationActive(customization)) {
            const { variation } = customization;
            if (variation && variation.option_values) {
              let price = variation.price_cents ? (variation.price_cents / 100) : 0;
              let hasLiner;
              let { description } = section;
              if (section.step === 'detailFront') {
                // if reverse printing is enabled, subtract 35 cents (price of reverse-printing) from front of card
                // so that pricing information for each step is more precise
                // postcard sized customizations do not allow reverse-printing to be false so always attribute the entire price
                // to front of card
                if (variation.option_values['reverse-printing'] === 'true' && variation.option_values.size !== 'postcard') {
                  price -= (BACK_PRINTING_PRICE / 100);
                }
              } else if (section.step === 'detailBack') {
                if (variation.option_values['reverse-printing'] === 'true' && variation.option_values.size !== 'postcard') {
                  price = BACK_PRINTING_PRICE / 100;
                } else if (variation.option_values['reverse-printing'] === 'true' && variation.option_values.size === 'postcard') {
                  // need to have this separate condition so that the description indicates that there is reverse-printing
                  // enabled
                  price = 0;
                  description = 'Print on the back (required)';
                } else {
                  price = 0;
                  description = 'No print on the back';
                }
              } else if (section.step === 'recipients') {
                price = 0;
              } else if (section.step === 'envelope') {
                if (variation.option_values['envelope-lining'] !== 'none') {
                  hasLiner = true;
                }
              }
              const mergedSection = { ...section, price, description, hasLiner, active: currentStep === section.step };
              result.push(mergedSection);
            }
          }
        }
      }
      return result;
    }, []);
    const sortedSections = sections.sort((a, b) => steps.orderedItems.indexOf(a.step) - steps.orderedItems.indexOf(b.step));
    return sortedSections;
  }
);

// new for sku migration

export const getLeadCustomization = createSelector(
  getProjectType,
  getCustomizationTypeUUIDs,
  getCustomizations,
  (projectType, customizationTypeToUUIDs, customizations) => {
    const uuid = customizationTypeToUUIDs[projectType];
    const leadCustomization = customizations[uuid];
    // console.log(leadCustomization);
    return leadCustomization;
  }
);

export const getQuantity = createSelector(
  getLeadCustomization,
  (customization) => {
    if (customization && customization.quantity) {
      return customization.quantity;
    }
    return 0;
  }
);

export const getCardForLeadCustomization = createSelector(
  getCards,
  getLeadCustomization,
  (cards, customization) => {
    if (cards && customization && customization.uuid) {
      const cardForLeadCustomization = _find(cards, { customizationUUID: customization.uuid });
      return cardForLeadCustomization;
    }
    return null;
  }
);

export const getLeadCardOptionGroups = createSelector(
  getCardForLeadCustomization,
  card => (card && card.option_groups ? card.option_groups : null)
);

export const getLeadCardOptionGroupsByKey = createSelector(
  getCardForLeadCustomization,
  card => (card && card.optionGroupsByKey ? card.optionGroupsByKey : null)
);

export const getLeadCardShapeOptions = createSelector(
  getLeadCardOptionGroupsByKey,
  optionGroupsByKey => (optionGroupsByKey && optionGroupsByKey.silhouette ? optionGroupsByKey.silhouette : null)
);

export const getLeadCardPaperTypeOptions = createSelector(
  getLeadCardOptionGroupsByKey,
  optionGroupsByKey => (optionGroupsByKey && optionGroupsByKey['paper-type'] ? optionGroupsByKey['paper-type'] : null)
);

export const getLeadVariationOptionValues = createSelector(
  getLeadCustomization,
  (customization) => {
    if (customization && customization.variation && customization.variation.option_values) {
      return customization.variation.option_values;
    }
    return null;
  }
);

export const getLeadVariationOptionValuesWithLabels = createSelector(
  getLeadCardOptionGroups,
  getLeadVariationOptionValues,
  (optionGroups, variationOptionValues) => {
    const initialObject = { values: {}, labels: {}, combined: {} };
    if (optionGroups) {
      const variationOptionValuesWithLabels = optionGroups.reduce((result, optionGroup) => {
        if (variationOptionValues[optionGroup.key]) {
          const option = _find(optionGroup.options, { value: variationOptionValues[optionGroup.key] });
          result.combined[optionGroup.key] = option; // eslint-disable-line
          result.values[optionGroup.key] = option.value; // eslint-disable-line
          result.labels[optionGroup.key] = option.label; // eslint-disable-line
        }
        return result;
      }, initialObject);
      return variationOptionValuesWithLabels;
    }
    return initialObject;
  }
);

export const getProjectOrderViewV2 = createSelector(
  getCustomizations,
  customizations => (
    Object.keys(customizations).reduce((result, uuid) => {
      const customization = customizations[uuid];
      if (customization && customization.variation) {
        const { variation } = customization;
        // make sure not to include any deleted customizations in order view
        // getProjectOrderView is used in subtotal selector so it's important to filter out deleted
        // customizations to avoid messing up subtotal
        if (variation && isCustomizationActive(customization)) {
          const orderView = {
            customizationUUID: customization.uuid,
            name: customization.name,
            type: customization.type,
            quantity: customization.quantity,
            recipientAddressing: customization.recipient_addressing,
            priceCents: variation.price_cents,
            imageUrls: variation.image_urls,
            color: variation.option_values.color,
            size: variation.option_values.size,
            liningColor: variation.option_values['envelope-lining'] || 'none',
          };
          result.push(orderView);
        }
      }
      return _sortBy(result, o => ORDERED_CUSTOMIZATION_TYPES.indexOf(o.type));
    }, [])
  )
);

export const getOrderSubtotal = createSelector(
  getCustomizations,
  customizations => (
    Object.keys(customizations).reduce((subtotal, uuid) => {
      const customization = customizations[uuid];
      const { variation } = customization;
      let cost = 0;
      // make sure not to include any deleted or inactive customizations in subtotal
      if (customization && variation && isCustomizationActive(customization)) {
        cost = ((customization.quantity * variation.price_cents) / 100);
      }
      return subtotal + cost;
    }, 0)
  )
);



// WEBPACK FOOTER //
// ./src/cards/selectors/Customization.js